import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  ProjectWorkspaceFileCategory,
  type ProjectWorkspaceFileDto,
  type ProjectDto,
  type InquiryDto,
  type OfferDto,
  ProjectCommentTargetKind,
  projectCommentAnchorKey,
} from '@paladin-monorepo/common';
import { projectsApi } from '@/lib/api/projects';
import { ProjectCommentsDialogTrigger } from '@/components/project/ProjectCommentsDialogTrigger';
import { useAuth } from '@/lib/auth';
import { isPrince2Account } from '@/lib/permissions';
import { ProjectManagementProductsCard } from '@/components/project-governance/ProjectManagementProductsCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Upload, Download, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const CATEGORY_ORDER = [
  ProjectWorkspaceFileCategory.CONTRACT,
  ProjectWorkspaceFileCategory.SCOPE,
  ProjectWorkspaceFileCategory.REPORTS,
  ProjectWorkspaceFileCategory.DATA,
  ProjectWorkspaceFileCategory.OTHER,
] as const;

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/acad',
  'image/png',
  'image/jpeg',
];

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function ProjectWorkspaceFilesPage() {
  const { t, i18n } = useTranslation();
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  const { project } = useOutletContext<{ project: ProjectDto; inquiry: InquiryDto; offer: OfferDto }>();
  const queryClient = useQueryClient();
  const { user } = useAuth({ middleware: 'auth', tmp: 'ProjectWorkspaceFilesPage' });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedUploadCategory, setSelectedUploadCategory] = useState<string>(
    ProjectWorkspaceFileCategory.CONTRACT,
  );
  const [isDragOver, setIsDragOver] = useState(false);

  const qk = ['projectWorkspaceFiles', projectId] as const;

  const { data: files = [], isLoading: filesLoading } = useQuery({
    queryKey: qk,
    enabled: !!projectId,
    queryFn: () => projectsApi.listWorkspaceFiles(projectId as string),
  });

  const { data: summariesDto } = useQuery({
    queryKey: ['project-comment-summaries', projectId],
    enabled: !!projectId,
    queryFn: () => projectsApi.getCommentSummaries(projectId as string),
  });

  const { data: activity = [], isLoading: activityLoading } = useQuery({
    queryKey: [...qk, 'activity'],
    enabled: !!projectId,
    queryFn: () => projectsApi.getWorkspaceFilesActivity(projectId as string),
  });

  const uploadMutation = useMutation({
    mutationFn: ({ file, category }: { file: File; category: string }) =>
      projectsApi.uploadWorkspaceFile(projectId as string, file, category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk });
      queryClient.invalidateQueries({ queryKey: [...qk, 'activity'] });
      queryClient.invalidateQueries({ queryKey: ['projectActivity', projectId] });
      toast.success(t('workspace.files.uploadSuccess'));
    },
    onError: (e: unknown) => {
      const msg = e && typeof e === 'object' && 'response' in e
        ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined;
      toast.error(msg || t('workspace.files.uploadError'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (fileId: string) => projectsApi.deleteWorkspaceFile(projectId as string, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk });
      queryClient.invalidateQueries({ queryKey: [...qk, 'activity'] });
      queryClient.invalidateQueries({ queryKey: ['projectActivity', projectId] });
      toast.success(t('workspace.files.deleteSuccess'));
    },
    onError: (e: unknown) => {
      const msg = e && typeof e === 'object' && 'response' in e
        ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
        : undefined;
      toast.error(msg || t('workspace.files.deleteError'));
    },
  });

  const byCategory = useMemo(() => {
    const m = new Map<string, ProjectWorkspaceFileDto[]>();
    for (const c of CATEGORY_ORDER) {
      m.set(c, []);
    }
    for (const f of files) {
      const list = m.get(f.category) ?? [];
      list.push(f);
      m.set(f.category, list);
    }
    return m;
  }, [files]);

  const nonEmptyCategories = useMemo(
    () => CATEGORY_ORDER.filter((c) => (byCategory.get(c) ?? []).length > 0),
    [byCategory],
  );

  const isEmployeeOnly = !user?.systemRole && user?.role === 'EMPLOYEE';

  const canDeleteFile = useCallback(
    (f: ProjectWorkspaceFileDto) =>
      f.isImmutable ? user?.systemRole === 'SUPERADMIN' : !isEmployeeOnly || !f.isImmutable,
    [isEmployeeOnly, user?.systemRole],
  );

  useEffect(() => {
    const commentId = searchParams.get('commentId');
    const targetKind = searchParams.get('targetKind');
    const targetId = searchParams.get('targetId');
    const fileId = searchParams.get('fileId');
    const scrollToId = targetKind === ProjectCommentTargetKind.WORKSPACE_FILE && targetId ? targetId : fileId;
    if (!scrollToId) {
      return;
    }
    const anchor = document.getElementById(`file-anchor-${scrollToId}`);
    anchor?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (commentId) {
      window.requestAnimationFrame(() => {
        document.getElementById(`comment-${commentId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, [searchParams]);

  const tryUpload = useCallback(
    (file: File, category: string) => {
      if (!projectId) return;

      if (file.size > 25 * 1024 * 1024) {
        toast.error(t('fileUpload.fileSizeExceeds', { maxSize: 25 }));
        return;
      }
      if (ACCEPTED_TYPES.length > 0 && !ACCEPTED_TYPES.includes(file.type)) {
        toast.error(t('fileUpload.fileTypeNotAllowed'));
        return;
      }

      uploadMutation.mutate({ file, category });
    },
    [projectId, t, uploadMutation],
  );

  const onFilePicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;
    tryUpload(file, selectedUploadCategory);
  };

  const onDropZoneDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    tryUpload(file, selectedUploadCategory);
  };

  const onDropZoneDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const onDropZoneDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const onDownload = async (f: ProjectWorkspaceFileDto) => {
    try {
      await projectsApi.downloadWorkspaceFile(project.id, f.id, f.originalName);
    } catch {
      toast.error(t('workspace.files.downloadError'));
    }
  };

  const onDelete = (f: ProjectWorkspaceFileDto) => {
    if (!canDeleteFile(f)) return;
    if (!window.confirm(t('workspace.files.deleteConfirm', { name: f.originalName }))) return;
    deleteMutation.mutate(f.id);
  };

  return (
    <div className="space-y-8">
      {isPrince2Account(user) && projectId ? <ProjectManagementProductsCard projectId={projectId} /> : null}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept={ACCEPTED_TYPES.join(',')}
        onChange={onFilePicked}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('workspace.files.dropZone.title')}</CardTitle>
          <CardDescription>{t('workspace.files.dropZone.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2 max-w-md">
            <Label htmlFor="workspace-file-category">{t('workspace.files.dropZone.categoryLabel')}</Label>
            <Select value={selectedUploadCategory} onValueChange={setSelectedUploadCategory}>
              <SelectTrigger id="workspace-file-category" className="w-full max-w-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_ORDER.map((c) => (
                  <SelectItem key={c} value={c}>
                    {t(`workspace.files.categories.${c}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedUploadCategory === ProjectWorkspaceFileCategory.CONTRACT && (
              <p className="text-xs text-muted-foreground">{t('workspace.files.immutableHint')}</p>
            )}
          </div>

          <button
            type="button"
            disabled={uploadMutation.isPending || !projectId}
            onClick={() => fileInputRef.current?.click()}
            onDrop={onDropZoneDrop}
            onDragOver={onDropZoneDragOver}
            onDragLeave={onDropZoneDragLeave}
            className={cn(
              'flex min-h-[140px] w-full flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center text-sm transition-colors',
              'border-muted-foreground/25 bg-muted/30 hover:bg-muted/50',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              isDragOver && 'border-primary bg-primary/5',
              (uploadMutation.isPending || !projectId) && 'pointer-events-none opacity-60',
            )}
          >
            {uploadMutation.isPending ? (
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            ) : (
              <Upload className="h-8 w-8 text-muted-foreground" />
            )}
            <span className="font-medium text-foreground">
              {isDragOver ? t('workspace.files.dropZone.dragActive') : t('workspace.files.dropZone.browse')}
            </span>
            <span className="text-muted-foreground text-xs max-w-md">
              {t(`workspace.files.categories.${selectedUploadCategory}`)}
            </span>
          </button>
        </CardContent>
      </Card>

      {filesLoading && (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('workspace.files.loading')}
        </div>
      )}

      {nonEmptyCategories.map((category) => {
        const rows = byCategory.get(category) ?? [];

        return (
          <Card key={category}>
            <CardHeader className="pb-2">
              <CardTitle>{t(`workspace.files.categories.${category}`)}</CardTitle>
              {category === ProjectWorkspaceFileCategory.CONTRACT && (
                <p className="text-sm text-muted-foreground">{t('workspace.files.immutableHint')}</p>
              )}
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('workspace.files.columns.name')}</TableHead>
                    <TableHead>{t('workspace.files.columns.size')}</TableHead>
                    <TableHead>{t('workspace.files.columns.uploadedBy')}</TableHead>
                    <TableHead>{t('workspace.files.columns.uploadedAt')}</TableHead>
                    <TableHead className="w-12 text-center">{t('workspace.files.columns.messages')}</TableHead>
                    <TableHead className="text-right w-[120px]">{t('workspace.files.columns.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((f) => {
                    const highlightCommentId = searchParams.get('commentId');
                    const highlightTargetId = searchParams.get('targetId');
                    const highlightKind = searchParams.get('targetKind');
                    const autoOpenComments =
                      !!highlightCommentId &&
                      highlightKind === ProjectCommentTargetKind.WORKSPACE_FILE &&
                      highlightTargetId === f.id;
                    const fileSummary =
                      summariesDto?.summaries[
                        projectCommentAnchorKey(ProjectCommentTargetKind.WORKSPACE_FILE, f.id)
                      ] ?? null;
                    return (
                      <TableRow key={f.id} id={`file-anchor-${f.id}`}>
                        <TableCell className="font-medium">{f.originalName}</TableCell>
                        <TableCell>{formatBytes(f.size)}</TableCell>
                        <TableCell>{f.uploadedBy.displayName}</TableCell>
                        <TableCell>
                          {new Date(f.uploadedAt).toLocaleString(i18n.language)}
                        </TableCell>
                        <TableCell className="text-center align-middle">
                          {projectId ? (
                            <div className="flex justify-center">
                              <ProjectCommentsDialogTrigger
                                projectId={projectId}
                                targetKind={ProjectCommentTargetKind.WORKSPACE_FILE}
                                targetId={f.id}
                                summary={fileSummary}
                                autoOpen={autoOpenComments}
                              />
                            </div>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          <Button type="button" variant="ghost" size="icon" onClick={() => onDownload(f)} title={t('workspace.files.download')}>
                            <Download className="h-4 w-4" />
                          </Button>
                          {canDeleteFile(f) && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              disabled={deleteMutation.isPending}
                              onClick={() => onDelete(f)}
                              title={t('workspace.files.delete')}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        );
      })}

      <Card>
        <CardHeader>
          <CardTitle>{t('workspace.files.activityTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {activityLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('workspace.files.loading')}
            </div>
          ) : activity.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('workspace.files.noActivity')}</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {activity.map((ev, i) => (
                <li key={`${ev.fileId}-${ev.kind}-${ev.at}-${i}`} className="flex flex-wrap gap-x-2 gap-y-1 border-b border-border/60 pb-2 last:border-0">
                  <span className="text-muted-foreground">
                    {new Date(ev.at).toLocaleString(i18n.language)}
                  </span>
                  <span className="font-medium">{ev.actor.displayName}</span>
                  <span>
                    {ev.kind === 'uploaded'
                      ? t('workspace.files.activityKind.uploaded')
                      : t('workspace.files.activityKind.deleted')}
                  </span>
                  <span className="text-muted-foreground">({t(`workspace.files.categories.${ev.category}`)})</span>
                  <span>{ev.originalName}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

ProjectWorkspaceFilesPage.displayName = 'ProjectWorkspaceFilesPage';
